import {
  CdnIntegration,
  CdnVendor,
  MutationResult,
} from "@modules/graphql/types"
import {
  HostingDetailsForSiteDocument,
  useConnectCloudCdnToSiteMutation,
} from "@modules/cdn/gatsbyCloudCdn/queries.generated"

import {
  SupportedCdnIntegrationsDocument,
  SupportedCdnIntegrationsQuery,
  SupportedCdnIntegrationsQueryVariables,
  useDeleteCdnFromSiteByVendorMutation,
} from "@modules/cdn/shared/queries.generated"

import { DataProxy } from "apollo-cache"

export function updateCDNIntegrationCache(
  proxy: DataProxy,
  siteId: string,
  update: (cdnIntegration: CdnIntegration) => CdnIntegration
) {
  const queryInfo = {
    query: SupportedCdnIntegrationsDocument,
    variables: {
      siteId: siteId,
    },
  }

  const queryData = proxy.readQuery<
    SupportedCdnIntegrationsQuery,
    SupportedCdnIntegrationsQueryVariables
  >(queryInfo)

  proxy.writeQuery<
    SupportedCdnIntegrationsQuery,
    SupportedCdnIntegrationsQueryVariables
  >({
    ...queryInfo,
    data: {
      ...queryData,
      supportedCDNIntegrations: queryData?.supportedCDNIntegrations?.map(
        update
      ),
    },
  })
}

export function useConnectCloudCdn(
  siteId: string
): {
  connectCloudCdn: () => Promise<MutationResult | null | undefined>
  disconnectCloudCdn: () => Promise<MutationResult | null | undefined>
} {
  const [connectCloudCdnToSiteMutation] = useConnectCloudCdnToSiteMutation()
  const [
    deleteCdnFromSiteByVendorMutation,
  ] = useDeleteCdnFromSiteByVendorMutation()

  const connectCloudCdn = async () => {
    const result = await connectCloudCdnToSiteMutation({
      variables: {
        siteId,
      },
      update: (proxy, { data }) => {
        if (!data?.connectCloudCdnToSite?.success) {
          return
        }

        updateCDNIntegrationCache(proxy, siteId, cdnIntegration => {
          if (
            cdnIntegration.vendor !== CdnVendor.CloudCdn &&
            cdnIntegration.connected === true
          ) {
            return { ...cdnIntegration, connected: false }
          }
          return cdnIntegration
        })
      },
      refetchQueries: [
        {
          query: HostingDetailsForSiteDocument,
          variables: { siteId },
        },
        {
          query: SupportedCdnIntegrationsDocument,
          variables: { siteId },
        },
      ],
    })

    return result?.data?.connectCloudCdnToSite
  }

  const disconnectCloudCdn = async () => {
    const result = await deleteCdnFromSiteByVendorMutation({
      variables: {
        siteId,
        vendor: CdnVendor.CloudCdn,
      },
      update: (proxy, { data }) => {
        if (!data?.deleteCDNFromSiteByVendor?.success) {
          return
        }

        updateCDNIntegrationCache(proxy, siteId, cdnIntegration => {
          if (
            cdnIntegration.vendor === CdnVendor.CloudCdn &&
            cdnIntegration.connected === true
          ) {
            return { ...cdnIntegration, connected: false }
          }
          return cdnIntegration
        })
      },
      refetchQueries: [
        {
          query: HostingDetailsForSiteDocument,
          variables: { siteId },
        },
      ],
    })

    return result?.data?.deleteCDNFromSiteByVendor
  }

  return {
    connectCloudCdn,
    disconnectCloudCdn,
  }
}
